import { useState, type ReactNode } from "react";
import { SECRET_SHAPE_GRAMMAR, workItemUrl, type PendingAction } from "@yagni/shared";
import { DecisionCard, type DecisionIntent } from "../decisions/DecisionCard.js";
import {
  useResolveWorkItemPendingAction,
  type CodePublicationFailure,
  type OpenWorkItemPendingAction,
} from "./useWorkItemPendingAction.js";
import { Button } from "../ui/button.js";
// The pending-action ask renders the canonical DecisionCard (R2), whose verb /
// decline / effect affordances are styled by the shared feed stylesheet
// (feed-act-*, feed-effect-line). Class-scoped (feed-*), so importing it here is
// safe wherever the panel mounts (case file, work-item detail panel).
import "../decisions/decisions.css";

interface WorkItemPendingActionPanelProps {
  open: OpenWorkItemPendingAction;
  onResolved?: () => void;
  /** The ticket's Team, so a GitHub reconnect returns to the ticket in its queue. */
  deskId?: string | null;
  /**
   * The case dock's quiet utilities (Archive, Next item), threaded into the
   * DecisionCard foot so the panel joins the dock grammar (utilities left,
   * primary at the right edge). Absent on pre-dock mounts (the work-item
   * detail panel), which keep the classic foot.
   */
  footStart?: ReactNode;
}

interface PendingActionDecision {
  /** Part 3 — the literal-consequence primary button. */
  verbLabel: string;
  /** Part 4 — the always-present after-line naming what resolving does. */
  effect: string;
  declineLabel: string;
  /** The honest risk read: `false` mutes the verb (internal write / held draft). */
  leavesWorkspace: boolean;
  /** `true` → confirm before firing (irreversible / high-blast). */
  requiresConfirmation: boolean;
}

/**
 * The five-part copy per pending-action type — the SAME shape the gate authors in
 * `gateVerbConfig`, keyed off the action's honest risk. Irreversible / high-blast
 * verbs (merge, deploy, delete) get a confirm; internal steps mute to one-tap.
 */
function pendingActionDecision(action: PendingAction): PendingActionDecision {
  if (action.payload?.kind === "code_candidate_materialization") {
    return {
      verbLabel: "Publish reviewed candidate",
      effect: "Publishing opens a pull request from this exact reviewed base and tree.",
      declineLabel: "Don't publish",
      leavesWorkspace: true,
      requiresConfirmation: true,
    };
  }
  switch (action.actionType) {
    case "send_email":
      return {
        verbLabel: "Approve & send",
        effect: "Sending delivers this from your connected account.",
        declineLabel: "Don't send",
        leavesWorkspace: true,
        requiresConfirmation: false,
      };
    case "merge_pr":
      return {
        verbLabel: "Approve & merge",
        effect: "Merging lands these changes on the base branch.",
        declineLabel: "Don't merge",
        leavesWorkspace: true,
        requiresConfirmation: true,
      };
    case "deploy":
      return {
        verbLabel: "Approve & deploy",
        effect: "Deploying ships this to the target environment.",
        declineLabel: "Don't deploy",
        leavesWorkspace: true,
        requiresConfirmation: true,
      };
    case "delete_data":
      return {
        verbLabel: "Approve deletion",
        effect: "This permanently removes the data it names.",
        declineLabel: "Don't delete",
        leavesWorkspace: true,
        requiresConfirmation: true,
      };
    case "create_resource":
      return {
        verbLabel: "Approve & create",
        effect: "Approving creates the resource it describes.",
        declineLabel: "Don't create",
        leavesWorkspace: true,
        requiresConfirmation: false,
      };
    case "tool_approval":
      return {
        verbLabel: "Approve",
        effect: "Approving lets the Team run this step.",
        declineLabel: "Don't run it",
        leavesWorkspace: false,
        requiresConfirmation: false,
      };
    default:
      return {
        verbLabel: "Approve & send",
        effect: "Approving lets the Team carry out this action.",
        declineLabel: "Don't send",
        leavesWorkspace: true,
        requiresConfirmation: false,
      };
  }
}

/**
 * The Work-side rendering of a staged draft (work-item spine, YAG-111 §8),
 * unified onto the ONE canonical DecisionCard (R2): a case file's pending-action
 * ask now reads with the SAME five-part shape / one verb ladder / after-line as
 * its gate ask — no bespoke second card. An EDITED proposal still reaches the
 * existing pending-action route (the FIT signal is never dropped): DecisionCard
 * owns the edited field-map and hands it to `onResolve` on approve.
 */
/**
 * One plain line on why the publication stopped: the scan's kinds and places,
 * then the server's sentence on what to do; else that sentence or the failure code.
 */
function publicationFailureLine(failure: CodePublicationFailure): string {
  const scanned = secretScanLine(failure);
  const sentence = failure.sentence?.trim();
  if (scanned) return sentence ? `${scanned} ${sentence}` : scanned;
  return sentence || `Publication stopped: ${failure.failureCode.replace(/_/g, " ")}.`;
}

/** What the secret scan flagged and where, or null when it did not stop this publication. */
function secretScanLine(failure: CodePublicationFailure): string | null {
  const words = (token: string) => token.replace(/_/g, " ");
  if (failure.failureCode === "candidate_secret_scan_failed" && failure.scanFindingKinds.length > 0) {
    // Only well-formed pairs: an older server sent bare strings here.
    const places = (failure.scanFindingLocations ?? []).filter(
      (place): place is { kind: string; location: string; shape?: string } =>
        !!place && typeof place === "object" && typeof place.kind === "string" && typeof place.location === "string",
    );
    if (places.length === 0) return `The secret scan flagged: ${failure.scanFindingKinds.map(words).join(", ")}.`;
    // Each kind stays with its own places; a kind the scan could not place is still named.
    const located = new Set(places.map((place) => place.kind));
    const parts = [
      ...places.map((place) =>
        // The shared grammar again: no other producer of locations can put text here.
        `${words(place.kind)} at ${place.location}${typeof place.shape === "string" && SECRET_SHAPE_GRAMMAR.test(place.shape) ? ` (${place.shape})` : ""}`),
      ...failure.scanFindingKinds.filter((kind) => !located.has(kind)).map(words),
    ];
    const omitted = failure.scanFindingLocationsOmitted ?? 0;
    const more = omitted > 0 ? `; and ${omitted} more` : "";
    return `The secret scan flagged: ${parts.join("; ")}${more}.`;
  }
  return null;
}

/**
 * Causes a retry of the same candidate can never clear: only a fresh build can.
 * `candidate_workflow_scan_failed` is not one: an earlier check held a change
 * back over the repository's own CI, which no longer stops a draft, so the
 * saved candidate retries as is.
 */
const REBUILD_ONLY_CODES: ReadonlySet<string> = new Set([
  "base_rewritten",
  "candidate_secret_scan_failed",
  "candidate_ci_change_not_in_plan",
  "candidate_path_not_allowed",
  "candidate_path_denied",
  "candidate_path_invalid",
  "candidate_tree_mismatch",
  "candidate_ancestry_invalid",
  "run_branch_conflict",
]);

/** Causes a person fixes in GitHub first: the App is gone, or was never connected. */
const RECONNECT_CODES: ReadonlySet<string> = new Set(["installation_gone", "github_installation_missing"]);

function minutesUntil(iso: string): number {
  return Math.max(1, Math.round((Date.parse(iso) - Date.now()) / 60_000));
}

/**
 * What a person can do about a stopped publication, by its cause: reopen the
 * plan when the same candidate would stop the same way, reconnect GitHub first
 * when the App is gone, else retry the exact candidate.
 */
function PublicationFailureActions({
  failureCode,
  publishAnyway,
  identity,
  workItemId,
  deskId,
  isResolving,
  onRetry,
}: {
  failureCode: string | null;
  /** The secret scan's hits are all random values next to sensitive names, each one named above. */
  publishAnyway: boolean;
  /** The candidate and its base → tree, already shortened. */
  identity: string;
  workItemId: string;
  deskId: string | null;
  isResolving: boolean;
  onRetry: (verb?: "retry" | "rebuild" | "publish-anyway") => void;
}) {
  if (failureCode && REBUILD_ONLY_CODES.has(failureCode)) {
    return (
      <>
        <Button className="mt-2" size="sm" onClick={() => onRetry("rebuild")} loading={isResolving}>
          Reopen plan approval
        </Button>
        {publishAnyway && (
          <>
            <p className="mt-3 text-[12px] text-[var(--text-secondary)]">
              Publish anyway only if none of the places named is a secret. The pull request lists them.
            </p>
            <Button className="mt-2" size="sm" variant="secondary" onClick={() => onRetry("publish-anyway")} disabled={isResolving}>
              Publish anyway
            </Button>
          </>
        )}
      </>
    );
  }
  const reconnect = failureCode !== null && RECONNECT_CODES.has(failureCode);
  return (
    <>
      <p className="text-[12px] text-[var(--text-secondary)]">
        Retry the exact approved candidate{identity}. The reviewed diff is not regenerated or changed.
      </p>
      {reconnect && (
        <a
          className="mt-2 mr-2 inline-flex h-9 md:h-7 items-center rounded-[var(--radius-control)] bg-accent px-md text-[13px] font-semibold text-text-inverse shadow-[var(--shadow-control)] hover:bg-accent-hover"
          href={`/api/auth/github/install?return_to=${encodeURIComponent(workItemUrl(workItemId, deskId))}`}
        >
          Reconnect GitHub
        </a>
      )}
      <Button
        className="mt-2"
        size="sm"
        variant={reconnect ? "secondary" : "primary"}
        onClick={() => onRetry()}
        loading={isResolving}
      >
        Retry publication
      </Button>
      <p className="mt-3 text-[12px] text-[var(--text-secondary)]">
        A fresh build requires another plan approval and produces a new candidate to review.
      </p>
      <Button className="mt-2" size="sm" variant="secondary" onClick={() => onRetry("rebuild")} disabled={isResolving}>
        Reopen plan approval
      </Button>
    </>
  );
}

export function WorkItemPendingActionPanel({
  open,
  onResolved,
  deskId = null,
  footStart,
}: WorkItemPendingActionPanelProps) {
  const { action, proposal, publicationFailure } = open;
  const { resolveAction, isResolving, error } = useResolveWorkItemPendingAction();
  const [locallyApprovedActionId, setLocallyApprovedActionId] = useState<string | null>(null);

  const awaitingShip = action.status === "approved" || locallyApprovedActionId === action.id;
  const isCodePublication = action.payload?.kind === "code_candidate_materialization";
  const config = pendingActionDecision(action);

  const handleResolve = async (
    intent: DecisionIntent,
    editedProposal?: Record<string, unknown> | null,
  ) => {
    try {
      // resolveAction sends the edit only on approve; DecisionCard passes it only
      // on approve too, so an unedited or declined resolve carries no editedProposal.
      const result = await resolveAction(action.id, intent, editedProposal ?? null);
      if (result.approvalRecorded) {
        setLocallyApprovedActionId(action.id);
        return;
      }
      onResolved?.();
    } catch {
      // error state rendered below; the action stays open for retry.
    }
  };

  const handleRetry = async (verb: "retry" | "rebuild" | "publish-anyway" = "retry") => {
    try {
      await resolveAction(action.id, verb);
      onResolved?.();
    } catch {
      // error state rendered below; the approved action remains retryable.
    }
  };

  const shortIdentity = (value: unknown): string | null =>
    typeof value === "string" && value.trim() ? value.trim().slice(0, 12) : null;
  const candidateId = shortIdentity(action.payload?.candidateId);
  const baseSha = shortIdentity(action.payload?.expectedBaseSha);
  const treeSha = shortIdentity(action.payload?.expectedTreeSha);

  return (
    <section
      className="mx-3 mt-3 rounded-[var(--radius-card)] border border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur)] backdrop-saturate-[1.15] shadow-[var(--shadow-staged)] p-3"
      data-testid="work-item-pending-action-panel"
    >
      <DecisionCard
        eyebrow={action.actionType.replace(/_/g, " ")}
        title={action.description}
        proposal={proposal}
        verbLabel={config.verbLabel}
        effect={config.effect}
        declineLabel={config.declineLabel}
        leavesWorkspace={config.leavesWorkspace}
        requiresConfirmation={config.requiresConfirmation}
        resolved={awaitingShip}
        receiptText={awaitingShip
          ? isCodePublication
            ? "Approval recorded, publication did not complete."
            : "Approved, awaiting ship."
          : undefined}
        undoDeadline={action.undoDeadline}
        onResolve={handleResolve}
        {...(footStart != null ? { footStart } : {})}
        fieldIdPrefix={`pending-${action.id}`}
        testIds={{
          root: "work-item-pending-action-card",
          proposal: "work-item-pending-action-proposal",
          verb: "work-item-pending-action-verb",
          decline: "work-item-pending-action-decline",
        }}
      />

      {awaitingShip && isCodePublication && (
        <div className="mt-3 rounded-[var(--radius-card)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] p-3">
          {publicationFailure?.retrying ? (
            // Wright is waiting out a transient cause; a press now would only
            // race its own next try.
            <>
              <p className="text-[12px] text-[var(--text-primary)]" data-testid="publication-retrying">
                {publicationFailureLine(publicationFailure)} Wright tries again on its own (try{" "}
                {publicationFailure.retrying.attempt} of {publicationFailure.retrying.maxAttempts}).
              </p>
              <p className="mt-1 text-[12px] text-[var(--text-secondary)]">
                Next try in about {minutesUntil(publicationFailure.retrying.nextAttemptAt)} min.
              </p>
            </>
          ) : (
            <>
              {publicationFailure && (
                <p className="mb-2 text-[12px] text-[var(--text-primary)]" data-testid="publication-failure">
                  {publicationFailureLine(publicationFailure)}
                </p>
              )}
              <PublicationFailureActions
                failureCode={publicationFailure?.failureCode ?? null}
                publishAnyway={publicationFailure?.publishAnyway === true}
                identity={`${candidateId ? ` ${candidateId}` : ""}${baseSha && treeSha ? ` · base ${baseSha} → tree ${treeSha}` : ""}`}
                workItemId={action.ticketId ?? ""}
                deskId={deskId}
                isResolving={isResolving}
                onRetry={(verb) => void handleRetry(verb)}
              />
            </>
          )}
        </div>
      )}

      {error && (
        <p className="mt-2 text-[12px] text-[var(--error)]">
          {error.message}
        </p>
      )}
    </section>
  );
}
